import { AlertTriangle, ArrowRight, CheckCircle2, Lightbulb, LoaderCircle, XCircle } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { CandlestickChart } from "../components/CandlestickChart";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";
import type { AnswerResult, JsonDocumentAnswer, PracticeSession, StructuredAnswer } from "../types";

const emptyStructured: StructuredAnswer = { state: "", evidence: [], confidence: "", invalidation: "", explanation: "" };

function isAnswerResult(value: unknown): value is AnswerResult {
  return Boolean(value && typeof value === "object" && "needs_review" in value);
}

const PREFLIGHT_LABELS = {
  information_boundary: { confirmed: "已确认", assumption: "含假设", unknown: "关键未知" },
  evidence_sufficiency: { sufficient: "充分", partial: "部分", insufficient: "不足" },
  confidence: { low: "低", medium: "中", high: "高" },
} as const;

function FrozenPreflight({ session }: { session: PracticeSession }) {
  const judgment = session.preflight_judgment;
  if (!judgment) return null;
  return <section className="frozen-preflight" aria-label="冻结的训练前判断">
    <div><strong>训练前判断已冻结</strong><span>案例 {judgment.case_id}</span></div>
    <dl>
      <div><dt>信息边界</dt><dd>{PREFLIGHT_LABELS.information_boundary[judgment.information_boundary]}</dd></div>
      <div><dt>主要风险</dt><dd>{judgment.primary_risks.join(" · ")}</dd></div>
      <div><dt>证据充分性</dt><dd>{PREFLIGHT_LABELS.evidence_sufficiency[judgment.evidence_sufficiency]}</dd></div>
      <div><dt>判断信心</dt><dd>{PREFLIGHT_LABELS.confidence[judgment.confidence]}</dd></div>
    </dl>
    <small>{judgment.submitted_at} · 内容哈希 {judgment.content_hash.slice(0, 12)}</small>
  </section>;
}

export function SessionPage() {
  const { sessionId = "" } = useParams();
  const { refreshPractice, refresh } = usePortal();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const [session, setSession] = useState<PracticeSession | null>(null);
  const [answer, setAnswer] = useState("");
  const [structured, setStructured] = useState<StructuredAnswer>(emptyStructured);
  const [jsonDocument, setJsonDocument] = useState<JsonDocumentAnswer | null>(null);
  const [hintLevel, setHintLevel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.session(sessionId).then(setSession).catch((reason) => setError(reason instanceof Error ? reason.message : String(reason))); }, [sessionId]);
  if (!session) return <div className="loading-state"><LoaderCircle className="spin" size={20} />{error || "正在恢复练习"}</div>;
  if (session.status === "completed") {
    const result = session.result!;
    return (
      <div className="session-complete">
        <span className="session-folio">{result.needs_review ? "待复核" : result.passed === false ? "未通过" : "已完成"}</span>
        <h1>{session.title}</h1>
        <div className="completion-score">{result.score == null ? "保存原答案" : `${result.score.toFixed(0)} 分`}</div>
        <p>{result.eligible_assessment ? "这次未见测试符合确定性证据条件，将由本机同步服务导入学习轨道。" : result.needs_review ? "开放回答已经保存，能力状态不会自动变化，等待 Codex 后续复核。" : "这是形成性练习，已经记录，但不作为掌握证据。"}</p>
        <FrozenPreflight session={session} />
        <div className="completion-actions"><Link className="command-button" to="/learn">返回训练中心</Link><button className="secondary-button" type="button" onClick={() => { void refreshPractice(); void refresh(); navigate("/progress"); }}>查看进度<ArrowRight size={17} /></button></div>
      </div>
    );
  }
  const item = session.current_item!;
  const result = item.result;
  const hasAnswer = item.response_type === "structured"
    ? Boolean(structured.state && structured.evidence.length && structured.confidence && structured.invalidation.trim().length >= 3)
    : item.response_type === "json_document" ? Boolean(jsonDocument)
    : Boolean(answer.trim());
  const canSubmit = capabilities.practice.writable && hasAnswer;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = item.response_type === "structured" ? structured
        : item.response_type === "json_document" ? jsonDocument!
          : item.response_type === "number" ? Number(answer) : answer;
      const next = await api.answer(sessionId, item.id, payload, hintLevel);
      setSession(next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function nextItem() {
    setBusy(true);
    try {
      const next = item.index === item.total
        ? await api.completeSession(sessionId)
        : await api.session(sessionId);
      setSession(next);
      setAnswer("");
      setStructured(emptyStructured);
      setJsonDocument(null);
      setHintLevel(0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="practice-shell">
      <header className="practice-header">
        <div><span>{session.qualifying ? "正式测试" : "形成性练习"}</span><h1>{session.title}</h1></div>
        <div className="question-count">{item.index}<small>/ {item.total}</small></div>
      </header>
      <div className="practice-progress"><span style={{ width: `${item.index / item.total * 100}%` }} /></div>
      <FrozenPreflight session={session} />
      <main className="question-panel">
        {item.image && <img className="case-image" src={item.image} alt="价格行为案例图" />}
        {item.chart && <CandlestickChart {...item.chart} />}
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.prompt}</ReactMarkdown>
        {item.case_inputs && <section className="formal-inputs" aria-label="冻结案例输入"><h2>冻结案例输入</h2><pre>{JSON.stringify(item.case_inputs, null, 2)}</pre></section>}
        {!result ? (
          <form onSubmit={(event) => void submit(event)}>
            {item.response_type === "json_document" ? (
              <label className="json-upload"><span>选择原始 JSON 产物</span><input type="file" accept="application/json,.json" onChange={(event) => {
                const file = event.target.files?.[0];
                setJsonDocument(null);
                setError(null);
                if (!file) return;
                void file.text().then(async (rawJson) => {
                  try {
                    const parsed = JSON.parse(rawJson);
                    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("JSON 根节点必须是对象");
                    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(rawJson));
                    const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
                    setJsonDocument({ file_name: file.name, raw_json: rawJson, sha256 });
                  } catch (reason) {
                    setError(reason instanceof Error ? reason.message : "JSON 文件无效");
                  }
                });
              }} />{jsonDocument && <small>{jsonDocument.file_name} · SHA-256 {jsonDocument.sha256.slice(0, 12)}</small>}</label>
            ) : item.response_type === "structured" ? (
              <div className="structured-answer">
                <fieldset><legend>当前状态</legend><div className="choice-list compact">{item.structured_fields?.states.map((option) => <label key={option} className={structured.state === option ? "is-selected" : ""}><input type="radio" name="state" value={option} checked={structured.state === option} onChange={() => setStructured((value) => ({ ...value, state: option }))} /><span>{option}</span></label>)}</div></fieldset>
                <fieldset><legend>直接证据（可多选）</legend><div className="evidence-grid">{item.structured_fields?.evidence.map((option) => <label key={option} className={structured.evidence.includes(option) ? "is-selected" : ""}><input type="checkbox" checked={structured.evidence.includes(option)} onChange={() => setStructured((value) => ({ ...value, evidence: value.evidence.includes(option) ? value.evidence.filter((item) => item !== option) : [...value.evidence, option].slice(0, 4) }))} /><span>{option}</span></label>)}</div></fieldset>
                <fieldset><legend>置信度</legend><div className="confidence-control">{item.structured_fields?.confidence.map((option) => <button key={option} type="button" className={structured.confidence === option ? "is-selected" : ""} onClick={() => setStructured((value) => ({ ...value, confidence: option as StructuredAnswer["confidence"] }))}>{option}</button>)}</div></fieldset>
                {item.structured_fields?.invalidation?.length ? (
                  <fieldset><legend>什么新信息会让当前判断失效？</legend><div className="choice-list compact">{item.structured_fields.invalidation.map((option) => <label key={option} className={structured.invalidation === option ? "is-selected" : ""}><input type="radio" name="invalidation" value={option} checked={structured.invalidation === option} onChange={() => setStructured((value) => ({ ...value, invalidation: option }))} /><span>{option}</span></label>)}</div></fieldset>
                ) : (
                  <label className="answer-field"><span>什么新信息会让当前判断失效？</span><textarea rows={3} value={structured.invalidation} onChange={(event) => setStructured((value) => ({ ...value, invalidation: event.target.value }))} /></label>
                )}
                {item.structured_fields?.freeform_explanation !== false && <label className="answer-field"><span>补充解释（不自动判分，原样保留）</span><textarea rows={4} value={structured.explanation} onChange={(event) => setStructured((value) => ({ ...value, explanation: event.target.value }))} /></label>}
              </div>
            ) : item.response_type === "choice" ? (
              <div className="choice-list">{item.options?.map((option) => <label key={option} className={answer === option ? "is-selected" : ""}><input type="radio" name="answer" value={option} checked={answer === option} onChange={(event) => setAnswer(event.target.value)} /><span>{option}</span></label>)}</div>
            ) : item.response_type === "number" ? (
              <label className="answer-field"><span>你的答案</span><div><input type="number" step="any" value={answer} onChange={(event) => setAnswer(event.target.value)} autoFocus /><b>{item.unit}</b></div></label>
            ) : (
              <label className="answer-field"><span>先写下自己的解释</span><textarea rows={7} value={answer} onChange={(event) => setAnswer(event.target.value)} autoFocus /></label>
            )}
            <div className="hint-area">
              {item.hints.slice(0, hintLevel).map((hint, index) => <p key={index}><Lightbulb size={16} />{hint}</p>)}
              {hintLevel < item.hints.length && <button className="hint-button" type="button" onClick={() => setHintLevel((value) => value + 1)}><Lightbulb size={16} />给我一级提示</button>}
            </div>
            {!capabilities.practice.writable && <p className="form-error" role="status"><AlertTriangle size={16} />当前为只读模式，不能提交答案。</p>}
            {error && <p className="form-error" role="alert"><AlertTriangle size={16} />{error}</p>}
            <button className="submit-answer" type="submit" disabled={!canSubmit || busy}>{busy ? "正在保存" : "提交答案"}<ArrowRight size={18} /></button>
          </form>
        ) : !isAnswerResult(result) ? (
          <section className="feedback-panel is-locked">
            <div className="feedback-title"><CheckCircle2 size={23} /><h2>答案已锁定</h2></div>
            <p>正式测试将在全部题目完成后统一显示总分；当前不会透露正确性、答案或解析。</p>
            <button className="submit-answer" type="button" onClick={() => void nextItem()} disabled={busy}>下一题<ArrowRight size={18} /></button>
          </section>
        ) : (
          <section className={`feedback-panel ${result.passed === false ? "is-wrong" : result.passed === null ? "is-unscored" : "is-correct"}`}>
            <div className="feedback-title">{result.needs_review || result.passed === null ? <AlertTriangle size={23} /> : result.passed ? <CheckCircle2 size={23} /> : <XCircle size={23} />}<h2>{result.needs_review ? "原答案已保存" : result.passed === null ? "未自动判分" : result.passed ? "判断正确" : "需要修正"}</h2></div>
            <p><strong>参考答案：</strong>{typeof result.correct_answer === "object" ? `${result.correct_answer.state}；证据：${result.correct_answer.evidence.join("、")}；置信度：${result.correct_answer.confidence}` : String(result.correct_answer)}</p>
            <p>{result.explanation}</p>
            <small>来源：{result.source_key}</small>
            {error && <p className="form-error" role="alert"><AlertTriangle size={16} />{error}</p>}
            <button className="submit-answer" type="button" onClick={() => void nextItem()} disabled={busy || (item.index === item.total && !capabilities.practice.writable)}>{item.index === item.total ? "完成本次练习" : "下一题"}<ArrowRight size={18} /></button>
            {!result.passed && !result.needs_review && <p className="correction-note">本题原答案已保留。继续完成后，系统会在后续练习中安排同构纠错，避免把看过答案的重做误算为未见证据。</p>}
          </section>
        )}
      </main>
    </div>
  );
}
